// src/console/lib/apiError.ts — reading the HTTP status back off a rejected
// request.
//
// ── WHY THIS EXISTS ──────────────────────────────────────────────────────
// The axios interceptor in services/apiService.ts collapses every failed
// response to an Error carrying a message string. That is fine for display and
// useless for deciding, because "Access Forbidden" and "Network Error" are the
// same type and a screen that wants to treat them differently has nothing to
// branch on.
//
// It now attaches `status` alongside the server `code`, and this is the reader.
// Branch on the status, never on the message — copy gets rewritten, and a
// screen whose behaviour is pinned to an exact string breaks silently when
// someone improves the wording.
import { isOperatorTwoFactorSetupRequired } from "../../services/stepUp";

/** The HTTP status of a failed request, when the interceptor preserved one. */
export function statusOf(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : undefined;
}

/**
 * A permission denial — the server understood the request and refused it.
 *
 * 403 only, deliberately NOT 401. They are different problems with different
 * fixes: a 401 is a dead or missing session, which the app resolves by
 * re-authenticating, and telling someone to "ask an admin for a permission"
 * when their session merely expired sends them somewhere useless.
 *
 * And NOT the 403 that says "set up your authenticator first"
 * (ADMIN_TWO_FACTOR_SETUP_REQUIRED). The operator holds the permission; the
 * server refused only because their own account has no factor yet. A screen
 * that branched on the status alone told them they lacked a permission they
 * have, and sent them to ask an admin for a grant that would change nothing.
 */
export function isPermissionError(error: unknown): boolean {
  return statusOf(error) === 403 && !isOperatorTwoFactorSetupRequired(error);
}

/**
 * Why a handle 409 happened, when the api said (`reason` beside `code` on the
 * error body): "taken", "changed_during_save" or "claim_in_progress". Any
 * other string is passed through for the caller to ignore; no other error
 * carries one.
 */
export function conflictReasonOf(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;
  const reason = (error as { reason?: unknown }).reason;
  return typeof reason === "string" ? reason : undefined;
}

/** The server `code` on a failed request, when one was sent. */
export function codeOf(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;
  const code = (error as { code?: unknown }).code;
  return typeof code === "string" ? code : undefined;
}

// ── customer-access gating (support.customer_access_gating_enabled) ─────────

/**
 * The api's refusal for an operator this customer is locked to
 * (middleware/requireCustomerAccess): a 403 carrying this code. A 403 with any
 * other code is a missing capability, and "request access to this customer"
 * would be the wrong advice for it, so the two are told apart on the code.
 *
 * ONE reader for every tab. Each customer section renders the same locked
 * notice off this, instead of an error alert or a toast.
 */
export const CUSTOMER_ACCESS_LOCKED_CODE = "CUSTOMER_ACCESS_LOCKED";

export const isCustomerAccessLocked = (error: unknown): boolean =>
  isPermissionError(error) && codeOf(error) === CUSTOMER_ACCESS_LOCKED_CODE;

/**
 * The per-operator limit on unscoped customer searches (user-lookup, referral
 * lookup, the client-list search): a 429 carrying this code. Every customer
 * search shows the same copy for it.
 */
export const CUSTOMER_LOOKUP_RATE_LIMITED_CODE = "CUSTOMER_LOOKUP_RATE_LIMITED";

export const isCustomerLookupRateLimited = (error: unknown): boolean =>
  statusOf(error) === 429 &&
  codeOf(error) === CUSTOMER_LOOKUP_RATE_LIMITED_CODE;

export const CUSTOMER_LOOKUP_RATE_LIMITED_COPY =
  "You've looked up a lot of customers in the last hour. Try again later, or ask a support lead.";

/**
 * The console's query retry policy: react-query's default three retries, except
 * for an answer that a retry cannot change: any 4xx. That covers a lookup
 * refused under the rate limit (429: a retry only spends more of the same hourly
 * limit) and a 403 (a missing capability, or CUSTOMER_ACCESS_LOCKED: the tab
 * should show its locked notice at once, not after three backed-off retries).
 * No status (offline) and 5xx keep the retries.
 */
export const retryUnlessRefused = (
  failureCount: number,
  error: unknown
): boolean => {
  const status = statusOf(error);
  if (status !== undefined && status >= 400 && status < 500) return false;
  return failureCount < 3;
};

/** The api's two refusals on extending an access grant, mapped to copy by the control. */
export const GRANT_EXTENSION_NOT_ALLOWED_CODE = "GRANT_EXTENSION_NOT_ALLOWED";
export const GRANT_LIFETIME_EXCEEDED_CODE = "GRANT_LIFETIME_EXCEEDED";

/**
 * The api refused a withdrawal because its bank account is not verified
 * (ENG-1382): staff create, re-point or manual approval while
 * `security.withdrawal.approve_verified_only` is armed. Nothing was reserved
 * on a create, and a refused approval leaves the withdrawal pending.
 */
export const DESTINATION_NOT_VERIFIED_CODE = "DESTINATION_NOT_VERIFIED";

/**
 * Decided by the 409 and the code together, never by the message: a 409 with
 * another code is a different conflict, the same code on another status is not
 * this refusal, and a sentence that merely mentions verification proves nothing.
 */
export function isDestinationNotVerified(error: unknown): boolean {
  return (
    statusOf(error) === 409 && codeOf(error) === DESTINATION_NOT_VERIFIED_CODE
  );
}

/**
 * The request never got an answer: no response at all (offline, DNS, a
 * dropped connection, a timeout). The interceptor passes these through as the
 * raw axios error, so `response` is what tells them apart from an HTTP refusal.
 * A request the app cancelled itself is not one.
 *
 * For a money write this is "we don't know", not "it failed": the request may
 * have reached the api and been applied.
 */
export function isNetworkFailure(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as {
    isAxiosError?: unknown;
    response?: unknown;
    code?: unknown;
  };
  return (
    e.isAxiosError === true && e.response == null && e.code !== "ERR_CANCELED"
  );
}

/**
 * The account itself is not active — blocked, or a signup that never completed
 * and is still `status: "disabled"`. `middleware/setContext` sends this code.
 *
 * A 401, but NOT a session problem: the session is perfectly valid and the
 * server still refuses, so signing in again cannot clear it. It needs its own
 * branch for the same reason a 403 needed one — the advice differs, and advice
 * that cannot work is worse than none.
 *
 * This is not hypothetical. An invited admin held a genuine role on an account
 * that was still disabled: their nav rendered from real capabilities and every
 * request behind it failed, presenting as a permissions bug that no amount of
 * granting could fix.
 */
export const ACCOUNT_NOT_ACTIVE_CODE = "ACCOUNT_NOT_ACTIVE";

export function isAccountInactiveError(error: unknown): boolean {
  return statusOf(error) === 401 && codeOf(error) === ACCOUNT_NOT_ACTIVE_CODE;
}

/**
 * A dead or missing session — the 401s that re-authenticating DOES fix.
 *
 * Explicitly excludes {@link isAccountInactiveError}, so the two never both
 * match and a caller cannot order its branches wrongly and silently tell a
 * disabled user to sign in again.
 */
export function isSessionError(error: unknown): boolean {
  return statusOf(error) === 401 && !isAccountInactiveError(error);
}

// ── global dual-control (admin.dual_control.global_enabled) ─────────────────
// Lived in console/pages/products/zar8/zar8OpsShared.ts until three unrelated
// surfaces needed it — ZAR8 ops, the provider rate cards, and now the trading
// pairs. It was never ZAR8-specific: `dualControlGate` sits at the end of every
// `capabilityGate`/`moneyOpGate` in the API, so any capability-gated mutation in
// the console can come back queued. zar8OpsShared re-exports these so its own
// callers are untouched.

/**
 * The `code` the API puts on each dual-control 409 (services/admin/
 * dualControlService.ts): a request was just filed, one is already pending, or
 * a signed approval exists for different values.
 */
export const DUAL_CONTROL_QUEUED_CODES = [
  "DUAL_CONTROL_SENT_FOR_APPROVAL",
  "DUAL_CONTROL_AWAITING_APPROVAL",
  "DUAL_CONTROL_PARAMS_CHANGED",
] as const;

/** The pre-code API's dual-control wording. Read ONLY when no specific code came back. */
const LEGACY_DUAL_CONTROL_MESSAGE =
  /^sent for approval|^awaiting approval|approvals queue|an approval exists for this target/i;

/**
 * When dual control applies, a gated mutation's FIRST attempt files an
 * AdminApprovalV2 request and returns 409 instead of running. That is a QUEUED
 * outcome, not a failure: after a senior approver signs off, re-sending the
 * byte-identical request consumes the approval and executes.
 *
 * Decided by the server `code`, never the copy. 409 alone cannot decide: the
 * same status carries genuine conflicts (a stale treasury quorum, a
 * double-execute, a week that already has a distribution), and calling one of
 * those "queued" would tell an operator to wait for a sign-off that is never
 * coming. Any other specific code is therefore NOT queued, whatever its message
 * says.
 *
 * Transitional: an API build from before the codes answers these 409s with the
 * generic `CONFLICT` (or no code). Only then is the old message shape read, so
 * a console deployed ahead of its API still shows "sent for approval". Remove
 * the fallback once every environment's API sends the codes.
 */
export const isDualControlQueued = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  if (statusOf(error) !== 409) return false;
  const code = codeOf(error);
  if ((DUAL_CONTROL_QUEUED_CODES as readonly string[]).includes(code ?? "")) {
    return true;
  }
  if (code !== undefined && code !== "CONFLICT") return false;
  return LEGACY_DUAL_CONTROL_MESSAGE.test(error.message);
};

/** Toast for the queued outcome (pages render the full server detail inline). */
export const DUAL_CONTROL_QUEUED_TOAST =
  "Sent for approval — a senior approver must sign off before this runs.";

/** Inline guidance shown with the server's queued message. */
export const DUAL_CONTROL_QUEUED_GUIDANCE =
  "A senior approver (support lead, CTO, head of finance, compliance or the " +
  "founder) must sign off in the Approvals queue. Once approved, run the " +
  "IDENTICAL action again — same values — and it will execute.";
